import React, { useMemo, useState } from 'react';
import type { SvgIconProps } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { screeningCountries } from 'utils/screeningCountries';
import { Phase } from './types';
import SaveTicket, { TicketControl } from './SaveTicket';

export type Tone = 'high' | 'medium' | 'low' | 'none';

interface QueryRowProps {
  glyph: React.ComponentType<SvgIconProps>;
  inputId: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  inputRef: (el: HTMLInputElement | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onClear?: () => void;
  clearLabel?: string;
  disabled?: boolean;
  isCode?: boolean;
  describedBy?: string;
  expanded?: boolean;
  controls?: string;
  token?: React.ReactNode;
  actions: React.ReactNode;
  menu?: React.ReactNode;
}

export const QueryRow: React.FC<QueryRowProps> = ({
  glyph,
  inputId,
  label,
  placeholder,
  value,
  onChange,
  inputRef,
  onKeyDown,
  onFocus,
  onClear,
  clearLabel = 'Clear the field',
  disabled = false,
  isCode = false,
  describedBy,
  expanded,
  controls,
  token,
  actions,
  menu,
}) => (
  <div className='psc-qrow'>
    <span className='psc-qglyph'>
      {React.createElement(glyph, { sx: { fontSize: 19 } })}
    </span>
    <label htmlFor={inputId} className='psc-sr'>
      {label}
    </label>
    <div className='psc-qfield'>
      <input
        id={inputId}
        ref={inputRef}
        type='text'
        className={`psc-q${isCode ? ' is-code' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete='off'
        disabled={disabled}
        aria-describedby={describedBy}
        aria-expanded={expanded}
        aria-controls={controls}
      />
      {value && !disabled && onClear && (
        <button
          type='button'
          className='psc-clear'
          onClick={onClear}
          aria-label={clearLabel}
          title={clearLabel}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </button>
      )}
    </div>
    {token}
    {actions}
    {menu}
  </div>
);

export interface SaveTicketSlot extends TicketControl {
  open: boolean;
  triggerRef: (el: HTMLButtonElement | null) => void;
  onToggle: () => void;
  onClose: () => void;
}

interface RunActionsProps {
  phase: Phase;
  canRun: boolean;
  cannotRunWhy: string;
  onRun: () => void;
  newQuery: boolean;
  ticket: SaveTicketSlot | null;
}

export const RunActions: React.FC<RunActionsProps> = ({
  phase,
  canRun,
  cannotRunWhy,
  onRun,
  newQuery,
  ticket,
}) => {
  const isRun = phase === 'run';
  const isDone = phase === 'done';
  return (
    <>
      {!isDone && !isRun && (
        <button
          type='button'
          className='psc-primary'
          onClick={onRun}
          disabled={!canRun}
          title={canRun ? undefined : cannotRunWhy}
        >
          Run screening
        </button>
      )}
      {isRun && (
        <span className='psc-running' role='status'>
          <span className='psc-dot psc-pulse' />
          Screening
        </span>
      )}
      {isDone && (
        <>
          {ticket && <SaveTicket {...ticket} />}
          <button
            type='button'
            className='psc-primary psc-primary--again'
            onClick={onRun}
            disabled={!canRun}
            title={canRun ? undefined : cannotRunWhy}
          >
            {!newQuery && <RefreshIcon sx={{ fontSize: 14 }} />}
            {newQuery ? 'Run screening' : 'Run again'}
          </button>
        </>
      )}
    </>
  );
};

interface RailChipProps {
  label: string;
  value: string;
  onClick: () => void;
  warn?: boolean;
  set?: boolean;
  pressed?: boolean;
  expanded?: boolean;
  disabled?: boolean;
  title?: string;
  tnum?: boolean;
  triggerRef?: (el: HTMLButtonElement | null) => void;
}

export const RailChip: React.FC<RailChipProps> = ({
  label,
  value,
  onClick,
  warn = false,
  set = false,
  pressed,
  expanded,
  disabled = false,
  title,
  tnum = false,
  triggerRef,
}) => (
  <button
    type='button'
    ref={triggerRef}
    className={`psc-chip${warn ? ' is-warn' : ''}${set ? ' is-set' : ''}`}
    onClick={onClick}
    aria-pressed={pressed}
    aria-expanded={expanded}
    disabled={disabled}
    title={title}
  >
    {label} <span className={`v${tnum ? ' tnum' : ''}`}>{value}</span>
  </button>
);

interface RailSegProps {
  label: string;
  options: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

export const RailSeg: React.FC<RailSegProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
}) => (
  <div className='psc-seg' role='group' aria-label={label}>
    {options.map((option) => (
      <button
        key={option.id}
        type='button'
        aria-pressed={value === option.id}
        onClick={() => onChange(option.id)}
        disabled={disabled}
      >
        {option.label}
      </button>
    ))}
  </div>
);

interface CountryPopProps {
  inputId: string;
  dialogLabel: string;
  searchPlaceholder: string;
  value: string;
  noneLabel: string;
  note: React.ReactNode;
  onPick: (value: string) => void;
}

export const CountryPop: React.FC<CountryPopProps> = ({
  inputId,
  dialogLabel,
  searchPlaceholder,
  value,
  noneLabel,
  note,
  onPick,
}) => {
  const [countryQuery, setCountryQuery] = useState('');
  const countries = useMemo(() => {
    const t = countryQuery.trim().toLowerCase();
    return screeningCountries
      .map((c: any) => c.name as string)
      .filter((n: string) => !t || n.toLowerCase().indexOf(t) !== -1);
  }, [countryQuery]);
  const pick = (next: string) => {
    setCountryQuery('');
    onPick(next);
  };
  return (
    <div className='psc-pop' role='dialog' aria-label={dialogLabel}>
      <div className='psc-pop-head'>
        <label htmlFor={inputId} className='psc-sr'>
          {searchPlaceholder}
        </label>
        <input
          id={inputId}
          type='search'
          className='psc-field'
          placeholder={searchPlaceholder}
          value={countryQuery}
          onChange={(e) => setCountryQuery(e.target.value)}
          autoFocus
        />
      </div>
      <div className='psc-pop-list'>
        {value && (
          <button type='button' className='psc-opt' onClick={() => pick('')}>
            <span className='check' />
            {noneLabel}
          </button>
        )}
        {countries.map((c) => (
          <button
            key={c}
            type='button'
            className={`psc-opt${value === c ? ' is-on' : ''}`}
            onClick={() => pick(c)}
          >
            <span className='check'>
              {value === c && <CheckIcon sx={{ fontSize: 14 }} />}
            </span>
            {c}
          </button>
        ))}
        {countries.length === 0 && (
          <div className='psc-pop-empty'>
            No country matches “{countryQuery}”.
          </div>
        )}
      </div>
      <div className='psc-pop-note'>{note}</div>
    </div>
  );
};

interface RecentsMenuProps {
  id: string;
  menuRef: React.RefObject<HTMLDivElement>;
  onExitTop: () => void;
  children: React.ReactNode;
}

export const recentMenuItems = (
  menu: HTMLDivElement | null,
): HTMLButtonElement[] =>
  Array.from(menu?.querySelectorAll<HTMLButtonElement>('.psc-recent') || []);

export const RecentsMenu: React.FC<RecentsMenuProps> = ({
  id,
  menuRef,
  onExitTop,
  children,
}) => {
  const onKeyDown = (e: React.KeyboardEvent) => {
    const items = recentMenuItems(menuRef.current);
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[Math.min(items.length - 1, at + 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (at <= 0) {
        onExitTop();
      } else {
        items[at - 1].focus();
      }
    }
  };
  return (
    <div
      id={id}
      ref={menuRef}
      className='psc-pop psc-pop--recents'
      role='dialog'
      aria-label='Recent screens'
      onKeyDown={onKeyDown}
    >
      <div className='psc-pop-label'>RECENT SCREENS</div>
      <div className='psc-pop-list'>{children}</div>
    </div>
  );
};

interface RecentRowProps {
  title: string;
  meta: string;
  tone: Tone;
  pill: string;
  count: string;
  when: string;
  onPick: () => void;
  className?: string;
  disabled?: boolean;
}

export const RecentRow: React.FC<RecentRowProps> = ({
  title,
  meta,
  tone,
  pill,
  count,
  when,
  onPick,
  className,
  disabled = false,
}) => (
  <button
    type='button'
    className={`psc-recent${className ? ` ${className}` : ''}`}
    onClick={onPick}
    disabled={disabled}
  >
    <span className='q'>{title}</span>
    {meta && <span className='meta'>{meta}</span>}
    <span className={`psc-pill psc-pill--${tone}`}>{pill}</span>
    <span className='psc-spacer' />
    <span className='meta'>{count}</span>
    <span className='when'>{when}</span>
  </button>
);
